import React, { useCallback, useRef } from "react";
import { View, StyleSheet, Pressable, Linking } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Skeleton } from "@/components/ui/Skeleton";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { useToolTip } from "@/hooks/useToolTip";
import useNavigationGuard from "@/hooks/useNavigationGuard";

export interface ContactFieldContact {
  id?: string;
  name: string;
  phone_number?: string | null;
  email_address?: string | null;
}

export interface ContactFieldProps {
  label?: string;
  contact?: ContactFieldContact | null;
  placeholder?: string;
  editing?: boolean;
  loading?: boolean;
  onPress?: () => void;
  params?: { panel: string; id: string };
  badge?: React.ReactNode;
  /** When true, shows only icons for phone/email with tooltips */
  compact?: boolean;
}

interface TooltipIconButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  color: string;
}

function TooltipIconButton({ icon, tooltip, onPress, color }: TooltipIconButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      // Use larger multiplier and minimum width for emails/phones
      const tooltipWidth = Math.max(tooltip.length * 8 + 24, 120);
      const tooltipHeight = 28;
      toolTip.setLocation({
        width: tooltipWidth,
        height: tooltipHeight,
        x: x + width / 2 - tooltipWidth / 2,
        y: y - tooltipHeight - 6,
      });
      toolTip.setToolTipContent(
        <View
          style={{
            backgroundColor: colors.background,
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 8,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: colors.border,
          }}
        >
          <ThemedText style={{ fontSize: 13 }}>{tooltip}</ThemedText>
        </View>,
      );
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      style={styles.tooltipIconButton}
    >
      <MaterialCommunityIcons name={icon} size={16} color={color} />
    </Pressable>
  );
}

export function ContactField({
  label,
  contact,
  placeholder = "No contact selected",
  editing = false,
  loading = false,
  onPress,
  params,
  badge,
  compact = false,
}: ContactFieldProps) {
  const { colors } = useTheme();
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleNavigation = () => {
    if (!params || editing) return;

    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;

    if (currentPanel && currentId) {
      pushNavigation({
        panel: currentPanel,
        id: currentId,
        title: currentEntity?.currentTitle || contact?.name || "",
      });
    }

    // Navigate to the entity
    navigateToEntity(params.panel, params.id);
  };

  // Loading skeleton
  if (loading) {
    return (
      <View style={styles.wrapper}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View style={[styles.container, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          <View style={styles.header}>
            <Skeleton width={36} height={36} radius={18} />
            <Skeleton width="50%" height={14} />
          </View>
          <View style={styles.details}>
            <Skeleton width="60%" height={12} />
            <Skeleton width="70%" height={12} />
          </View>
        </View>
      </View>
    );
  }

  // Empty state
  if (!contact) {
    return (
      <View style={styles.wrapper}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View style={styles.row}>
          <View
            style={[styles.emptyContainer, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
          >
            <MaterialCommunityIcons name="account-outline" size={20} color={colors.placeholder} />
            <ThemedText style={[styles.placeholder, { color: colors.placeholder }]}>{placeholder}</ThemedText>
          </View>
          {editing && onPress && (
            <Button
              text="Select"
              icon="account-search"
              iconPosition="right"
              iconColor="white"
              textStyle={{ color: "white" }}
              style={[styles.button, { backgroundColor: colors.primary }]}
              onPress={onPress}
            />
          )}
        </View>
      </View>
    );
  }

  // Parse name for profile pic
  const nameParts = contact.name.split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  const isClickable = params && !editing;

  return (
    <View style={styles.wrapper}>
      {label && <ThemedText style={styles.label}>{label}</ThemedText>}
      <View style={styles.row}>
        <View style={[styles.container, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          {/* Profile pic */}
          <Pressable
            onPress={isClickable ? handleNavigation : undefined}
            disabled={!isClickable}
            style={({ hovered }) => [hovered && isClickable && { opacity: 0.8 }]}
          >
            <Avatar firstName={firstName} lastName={lastName} size={32} />
          </Pressable>

          {/* Name and badge */}
          <Pressable
            onPress={isClickable ? handleNavigation : undefined}
            disabled={!isClickable}
            style={({ hovered }) => [styles.nameContainer, hovered && isClickable && { opacity: 0.8 }]}
          >
            <ThemedText
              style={[styles.name, isClickable && { color: colors.primary }]}
              type={isClickable ? "link" : "default"}
            >
              {contact.name}
            </ThemedText>
            {badge}
          </Pressable>

          {/* Phone and Email links */}
          {compact ? (
            <View style={styles.compactActions}>
              {contact.phone_number && (
                <TooltipIconButton
                  icon="phone"
                  tooltip={contact.phone_number}
                  onPress={() => Linking.openURL(`tel:${contact.phone_number}`)}
                  color={colors.primary}
                />
              )}
              {contact.email_address && (
                <TooltipIconButton
                  icon="email"
                  tooltip={contact.email_address}
                  onPress={() => Linking.openURL(`mailto:${contact.email_address}`)}
                  color={colors.primary}
                />
              )}
            </View>
          ) : (
            <>
              {contact.phone_number && (
                <Button
                  icon="phone"
                  iconSize={14}
                  text={contact.phone_number}
                  onPress={() => Linking.openURL(`tel:${contact.phone_number}`)}
                  style={{ backgroundColor: "transparent", borderWidth: 0, gap: 4 }}
                  iconStyle={{ color: colors.primary }}
                  textStyle={{ color: colors.primary, fontSize: 13, fontWeight: "500" }}
                />
              )}
              {contact.email_address && (
                <Button
                  icon="email"
                  iconSize={14}
                  text={contact.email_address}
                  onPress={() => Linking.openURL(`mailto:${contact.email_address}`)}
                  style={{ backgroundColor: "transparent", borderWidth: 0, gap: 4 }}
                  iconStyle={{ color: colors.primary }}
                  textStyle={{ color: colors.primary, fontSize: 13, fontWeight: "500" }}
                />
              )}
            </>
          )}
        </View>
        {editing && onPress && (
          <Button
            text="Change"
            icon="account-search"
            iconPosition="right"
            iconColor="white"
            textStyle={{ color: "white" }}
            style={[styles.button, { backgroundColor: colors.primary }]}
            onPress={onPress}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    // marginBottom: 12,
  },
  label: {
    fontSize: 14,
    marginBottom: 4,
    fontWeight: "600",
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 8,
  },
  container: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    gap: 12,
    minHeight: 40,
    maxHeight: 40,
  },
  emptyContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    minHeight: 40,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  nameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  name: {
    fontSize: 14,
    fontWeight: "500",
  },
  details: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  placeholder: {
    fontSize: 14,
  },
  button: {
    height: 40,
    gap: 8,
    paddingHorizontal: 12,
  },
  tooltipIconButton: {
    padding: 4,
    borderRadius: 4,
  },
  compactActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
});
